/**
 * Who may open the AI Review prompt viewer.
 *
 * ONE switch, mirrored by the API's `PROMPT_VIEWER_AUDIENCE` in
 * `packages/api/app/auth/deps.py`: `"all"` shows it to every signed-in user,
 * `"admin"` only to users whose `role` is in `ADMIN_ROLES`. Flip both together
 * — the API is the gate; this one only hides an entry the API would refuse.
 */
import { useMe } from "@nu-assist/shared-fe";

export type PromptViewerAudience = "all" | "admin";

export const PROMPT_VIEWER_AUDIENCE: PromptViewerAudience = "all";

export const ADMIN_ROLES: ReadonlySet<string> = new Set(["admin"]);

/** Pure rule behind `usePromptViewerAllowed`, so both audiences are testable. */
export function isPromptViewerAllowed(
  audience: PromptViewerAudience,
  signedIn: boolean,
  role: string | null | undefined
): boolean {
  if (!signedIn) return false;
  if (audience === "all") return true;
  return role != null && ADMIN_ROLES.has(role.trim().toLowerCase());
}

export function usePromptViewerAllowed(
  audience: PromptViewerAudience = PROMPT_VIEWER_AUDIENCE
): boolean {
  const me = useMe();
  return isPromptViewerAllowed(
    audience,
    me.status === "authenticated",
    me.status === "authenticated" ? me.data.role : null
  );
}
